import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import B2bTDPAPI from 'api/b2b/TdpApi'
import WalletStatementsApi, {
  IWalletStatementBackendData
} from 'api/b2b/WalletStatement'
import OrbitSession from 'hooks/useCookies'

import WalletStatementDetailedList from './WalletStatementDetailedList'
import WalletStatementList from './WalletStatementList'

interface Company {
  tenant_id: string
  company_name: string
  id: string
}

interface TenantData {
  data: Company[]
}

interface CompanyOption {
  label: string
  value: string
  companyId: string
}

interface FormData {
  companyName: CompanyOption | null
  CompanyID: string
  orderStartDate: string
  orderEndDate: string
}

export interface WalletStatementApiResponse {
  status: string
  status_code: number
  message: string
  data: {
    wallet_statement_details: WalletStatementDetailedApiResponse[]
    transaction_date_range: string
    total_transactions: number
    opening_balance: number
    total_debit: number
    total_credit: number
    closing_balance: number
  }
  error: string
  timestamp: string
}

export interface WalletStatementDetailedApiResponse {
  company_name: string
  transaction_date_time: string
  transaction_type: string
  transaction_id: string
  booking_id: string
  booking_date: string
  product: string
  debit: number
  credit: number
  wallet_balance: number
  mode: string
  comment: string
}

export default function WalletStatement() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [tableData, setTableData] = useState<any>(null)
  const [detailedWalletTableData, setDetailedWalletTableData] = useState<
    WalletStatementDetailedApiResponse[]
  >([])

  const {
    data: tenantData,
    isLoading: isTenantLoading,
    isError: tenantFetchError,
    refetch: refetchTenant
  } = useQuery<TenantData>(
    ['tenant-detail'],
    async () => {
      const token = OrbitSession.get('faberToken')
      const { data } = await B2bTDPAPI.getTDPDetails(token)
      return data
    },
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  )

  const { mutate: getWalletStatement, isLoading } = useMutation({
    mutationFn: async (searchTerm: IWalletStatementBackendData) => {
      const token = OrbitSession.get('faberToken')
      return WalletStatementsApi.getWalletStatement(token, searchTerm)
    },
    onSuccess: (response: WalletStatementApiResponse) => {
      if (response?.status === 'success' && response?.data) {
        toast.success('Wallet statement fetched successfully')
        setTableData(response.data)
        setDetailedWalletTableData(response.data.wallet_statement_details)
        // Pass only the data object
      } else {
        toast.info('No data found ', {
          autoClose: 2000,
          hideProgressBar: false
        })
        setTableData(null)
      }
    },
    onError: (error) => {
      toast.error('Failed to fetch wallet statement')
      console.error('API Error:', error)
      setTableData(null)
    }
  })

  const { handleSubmit, reset, control, setValue, watch } = useForm<FormData>({
    defaultValues: {
      companyName: null,
      CompanyID: '',
      orderStartDate: '',
      orderEndDate: ''
    }
  })

  const companyNameInput = watch('companyName')

  const companyOptions = useMemo((): CompanyOption[] => {
    if (!tenantData || !Array.isArray(tenantData)) return []

    return tenantData.map((company: Company) => ({
      label: company.company_name || '',
      value: company.company_name || '',
      companyId: company.id || ''
    }))
  }, [tenantData])

  useEffect(() => {
    if (companyNameInput?.companyId) {
      setValue('CompanyID', companyNameInput.companyId)
    } else {
      setValue('CompanyID', '')
    }
  }, [companyNameInput, setValue])

  const filterOptions = useCallback(
    (options: CompanyOption[], { inputValue }: { inputValue: string }) => {
      if (!inputValue) return options.slice(0, 100)

      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      )
      return filtered.slice(0, 100)
    },
    []
  )

  const onSubmit = (data: FormData) => {
    const cleanedData: IWalletStatementBackendData = {
      company_name: data.companyName?.value || '',
      company_id: data.CompanyID
    }

    if (data.orderStartDate) {
      cleanedData.from_date = data.orderStartDate
    }
    if (data.orderEndDate) {
      cleanedData.to_date = data.orderEndDate
    }

    getWalletStatement(cleanedData)
  }

  const handleReset = () => {
    reset({
      companyName: null,
      CompanyID: '',
      orderStartDate: '',
      orderEndDate: ''
    })
    setTableData(null)
    setDetailedWalletTableData([])
  }

  if (isTenantLoading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading company data...</Typography>
      </Box>
    )
  }

  if (tenantFetchError) {
    return (
      <Box>
        <Typography color='error'>Failed to load company data</Typography>
        <Button
          onClick={() => refetchTenant()}
          variant='outlined'
          sx={{ mt: 1 }}
        >
          Retry
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 2,
          p: 3,
          bgcolor: '#f7faff',
          borderRadius: 2
        }}
      >
        <Controller
          name='companyName'
          control={control}
          rules={{
            required: { value: true, message: 'Company name is required' }
          }}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              disablePortal
              options={companyOptions}
              filterOptions={filterOptions}
              loading={isTenantLoading}
              getOptionLabel={(option: CompanyOption | null) =>
                option?.label || ''
              }
              isOptionEqualToValue={(
                option: CompanyOption,
                value: CompanyOption | null
              ) => option?.value === value?.value}
              onChange={(_, data: CompanyOption | null) => field.onChange(data)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Company Name *'
                  placeholder='Select company name'
                  variant='outlined'
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isTenantLoading ? (
                          <CircularProgress color='inherit' size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          )}
        />

        <Controller
          name='CompanyID'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label='Company ID'
              placeholder='Auto-populated'
              variant='outlined'
              fullWidth
              disabled={true}
              sx={{
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)'
                }
              }}
            />
          )}
        />

        <Controller
          name='orderStartDate'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              type='date'
              label='Order Start Date'
              InputLabelProps={{ shrink: true }}
              variant='outlined'
              fullWidth
            />
          )}
        />

        <Controller
          name='orderEndDate'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              type='date'
              label='Order End Date'
              InputLabelProps={{ shrink: true }}
              variant='outlined'
              fullWidth
            />
          )}
        />

        <Box sx={{ gridColumn: '1 / -1' }}>
          <Divider sx={{ my: 1 }} />
        </Box>

        <Box
          sx={{
            gridColumn: '1 / -1',
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end'
          }}
        >
          <Button
            variant='outlined'
            color='secondary'
            onClick={handleReset}
            type='button'
            disabled={isLoading}
          >
            Reset
          </Button>
          <Button
            variant='contained'
            color='primary'
            type='submit'
            disabled={!companyNameInput?.value || isLoading}
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Searching...
              </>
            ) : (
              'Search'
            )}
          </Button>
        </Box>
      </Box>
      <Box>
        <WalletStatementList walletStatementData={tableData} />
      </Box>
      <Box>
        <WalletStatementDetailedList data={detailedWalletTableData} />
      </Box>
    </Box>
  )
}
